/**
 * The admin app shell (design-system.md §3 Spacing & layout, §6).
 *
 * Three pieces: the side rail (240px labelled, 64px icon-only below 1024px), a
 * centred 1240px page container, and the page header inside it. Screens supply
 * their own content and wrap it in `<FrameCard>` where the design calls for the
 * white frame.
 */
import type { ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import Box from '@mui/material/Box';
import { isQaSeedNavEnabled } from '../../config/qa-seed';
import {
  AssetsIcon,
  CatalogueIcon,
  ConfigurationIcon,
  ContestsIcon,
  OperationsIcon,
  PrizeTiersIcon,
  QaSeedIcon,
  SportsIcon,
  UsersIcon,
} from '../ui';
import { AdminNavRail } from './admin-nav-rail';
import type { NavItem } from './admin-nav-rail';
import { AdminPageHeader } from './admin-page-header';
import { useNavRailExpanded } from './use-nav-rail-expanded';
import type { HeaderAction } from './header-action';

export type { HeaderAction } from './header-action';

interface AdminLayoutProps {
  title: string;
  titleBadge?: ReactNode;
  subtitle?: string;
  displayName?: string;
  headerActions?: HeaderAction[];
  /**
   * Actions folded into a `⋯` menu after the visible ones — for what belongs to
   * the screen but not in the design's header. See `HeaderOverflowMenu`.
   */
  headerOverflowActions?: HeaderAction[];
  /** Names that menu, e.g. "More contest actions". */
  headerOverflowLabel?: string;
  /** The menu trigger's test id — per screen, since every page shares this header. */
  headerOverflowTestId?: string;
  /** The row above the title — status pill, age chip, event date, ID chip. */
  meta?: ReactNode;
  /**
   * The row opposite the title, beside the header actions — `.cat-head-meta`,
   * the Catalogue's feed status pill. See `AdminPageHeader`.
   */
  headerAside?: ReactNode;
  /**
   * How wide the page container is allowed to run.
   *
   * `default` is the 1240px most screens are drawn at — Catalogue and Reels
   * included, so a data table is not on its own a reason to go wide.
   *
   * `wide` is 1440px. In the handoff exactly one page asks for it: `.cl-page`,
   * the Contests list, against `.cat-page` / `.rl-page` / `.page` at 1240.
   *
   * **It started as two screens**, the Contests list and the contest detail
   * page, for the same symptom from opposite directions. The handoff's own
   * pages are standalone captures at ~1512px, where 1240 nearly fills the 1272
   * beside the 240px rail; past that width the container stops growing and the
   * content becomes a band floating in the middle of the display. The Contests
   * list hits it at any width because of its eight columns; the contest detail
   * page hits it on an ordinary desktop. Reviewed and settled on CAE-1546 —
   * `pages/contests/README.md` carries the measurements and the trade-off.
   *
   * > **CAE-1556 made it the admin's width, not three screens'.** Edit Contest
   * > had joined the other two, so all three contest screens ran at 1440 while
   * > Operations, Users, Assets, Configuration, Prize tiers and QA seed ran at
   * > 1240 — and a screen whose content starts 100px further in than the one
   * > you navigated from reads as a different page template, which is the seam
   * > that ticket exists to close. Every full-page screen is `wide` now.
   * >
   * > `default` is kept rather than removed: it is still what the handoff
   * > draws, and a future screen with a genuinely narrow measure should be
   * > able to say so. But a new screen should match its neighbours unless it
   * > has a reason not to.
   */
  width?: 'default' | 'wide';
  children: ReactNode;
}

const BASE_NAV_ITEMS: readonly NavItem[] = [
  { to: '/contests', label: 'Contests', Icon: ContestsIcon },
  { to: '/operations', label: 'Operations', Icon: OperationsIcon },
  { to: '/prize-tier-defaults', label: 'Prize tiers', Icon: PrizeTiersIcon },
  { to: '/sports', label: 'Sports', Icon: SportsIcon },
  { to: '/catalogue', label: 'Catalogue', Icon: CatalogueIcon },
  { to: '/assets', label: 'Assets', Icon: AssetsIcon },
  { to: '/users', label: 'Users', Icon: UsersIcon },
  { to: '/configuration', label: 'Configuration', Icon: ConfigurationIcon },
];

const QA_SEED_NAV_ITEM: NavItem = {
  to: '/qa-seed',
  label: 'QA seed',
  Icon: QaSeedIcon,
};

/**
 * Resolved per render rather than once at module load: `isQaSeedNavEnabled()`
 * reads the environment, and pinning its answer to import time makes the
 * conditional untestable and the rail unable to react if the flag ever moves.
 */
const navItems = (): readonly NavItem[] =>
  isQaSeedNavEnabled() ? [...BASE_NAV_ITEMS, QA_SEED_NAV_ITEM] : BASE_NAV_ITEMS;

export const AdminLayout = ({
  title,
  titleBadge,
  subtitle,
  displayName = 'Admin',
  headerActions = [],
  headerOverflowActions = [],
  headerOverflowLabel,
  headerOverflowTestId,
  meta,
  headerAside,
  width = 'default',
  children,
}: AdminLayoutProps) => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navRail = useNavRailExpanded();
  // Every slot counts, not just the title: screens that pass `title=""` and let
  // their content carry the heading still hand the header badges and actions,
  // and a slot left out here disappears with nothing to show for it.
  const showPageHeader =
    Boolean(title.trim()) ||
    Boolean(subtitle?.trim()) ||
    headerActions.length > 0 ||
    headerOverflowActions.length > 0 ||
    Boolean(meta) ||
    Boolean(headerAside) ||
    Boolean(titleBadge);

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        bgcolor: theme.tokens.color.bg,
      })}
    >
      <AdminNavRail
        items={navItems()}
        pathname={pathname}
        displayName={displayName}
        expanded={navRail.expanded}
        canToggle={navRail.canToggle}
        onToggle={navRail.toggle}
      />
      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {/*
          The max-width is the outer box, padding included — so the default 1240
          carries 1176 of content. That is what the handoff does too: its pages
          set `max-width` with the same `32px 32px 64px` under a global
          `box-sizing: border-box`. Not a rounding slip to "fix" by adding the
          padding back on.
        */}
        <Box
          sx={(theme) => {
            const {
              pageMaxWidth,
              pageMaxWidthWide,
              pagePaddingX,
              pagePaddingTop,
              pagePaddingBottom,
              cardGap,
            } = theme.tokens.layout;
            return {
              maxWidth: width === 'wide' ? pageMaxWidthWide : pageMaxWidth,
              mx: 'auto',
              px: `${pagePaddingX}px`,
              pt: `${pagePaddingTop}px`,
              pb: `${pagePaddingBottom}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: `${cardGap}px`,
            };
          }}
        >
          {showPageHeader ? (
            <AdminPageHeader
              title={title}
              titleBadge={titleBadge}
              subtitle={subtitle}
              meta={meta}
              aside={headerAside}
              actions={headerActions}
              overflowActions={headerOverflowActions}
              overflowLabel={headerOverflowLabel}
              overflowTestId={headerOverflowTestId}
            />
          ) : null}
          {children}
        </Box>
      </Box>
    </Box>
  );
};
